import { useState, useEffect } from "react";
import api from "../lib/api";
import { Sparkles, Loader2, Clock, Library, Flame, Target, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "score", label: "Score Viralité IA", icon: Target },
  { id: "hours", label: "Heures Optimales", icon: Clock },
  { id: "structures", label: "Structures Virales", icon: Library },
  { id: "trends", label: "Tracker Tendances", icon: Flame },
];

export default function MillionViewsLab() {
  const [tab, setTab] = useState("score");

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="mvl-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// MILLION VIEWS LAB</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Le laboratoire à viralité</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">4 outils pour pré-tester un contenu, optimiser tes heures, copier les structures qui marchent, surveiller les trends en temps réel.</p>
      </header>

      <div className="border-b border-[#1E1E24] flex gap-0 overflow-x-auto scrollbar-thin">
        {TABS.map(t => {
          const I = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`mvl-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-mono uppercase tracking-wider transition border-b-2 ${
                tab === t.id ? "text-[#CCFF00] border-[#CCFF00]" : "text-zinc-500 border-transparent hover:text-zinc-300"
              }`}>
              <I className="w-4 h-4" strokeWidth={1.5} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "score" && <ScoreVirality />}
      {tab === "hours" && <OptimalHours />}
      {tab === "structures" && <ViralStructures />}
      {tab === "trends" && <TrendsTracker />}
    </div>
  );
}

// ============== Score Viralité ==============
function ScoreVirality() {
  const [form, setForm] = useState({ hook: "", concept: "", platform: "tiktok", niche: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const score = async () => {
    if (!form.hook) return toast.error("Hook requis");
    setLoading(true); setResult(null);
    try {
      const r = await api.aiScoreVirality(form);
      const m = (r.raw || "").match(/\{[\s\S]*\}/);
      if (m) setResult(JSON.parse(m[0]));
      else toast.error("Format IA invalide");
    } catch { toast.error("Erreur IA"); }
    finally { setLoading(false); }
  };

  const verdictColor = (v) => ({ EXCELLENT: "#10B981", FORT: "#CCFF00", MOYEN: "#F59E0B", FAIBLE: "#EF4444" }[v] || "#A1A1AA");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
      <div className="lg:col-span-2 border border-[#00D4FF]/30 bg-[#00D4FF]/[0.02] rounded-sm p-6 space-y-3">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.5} />
          <h2 className="font-display text-xl font-semibold text-white">Pré-test viralité</h2>
        </div>
        <p className="text-xs text-zinc-400">Colle un hook ou un concept. L'IA score 8 dimensions et donne 3 versions améliorées.</p>
        <textarea value={form.hook} onChange={e => setForm({ ...form, hook: e.target.value })}
          placeholder="Hook (ex: Personne ne te dira ça mais...)" rows={3} data-testid="score-hook"
          className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#00D4FF] rounded-sm px-3 py-2 text-sm text-white outline-none resize-none" />
        <textarea value={form.concept} onChange={e => setForm({ ...form, concept: e.target.value })}
          placeholder="Concept vidéo (optionnel : structure, durée, format)" rows={2}
          className="w-full bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white outline-none resize-none" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
            className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="tiktok">TikTok</option><option value="instagram">Instagram</option>
            <option value="youtube">YouTube Shorts</option><option value="threads">Threads</option><option value="x">X</option>
          </select>
          <input value={form.niche} onChange={e => setForm({ ...form, niche: e.target.value })}
            placeholder="Niche" className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white outline-none" />
        </div>
        <button onClick={score} disabled={loading} data-testid="score-btn"
          className="w-full bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 px-4 py-2.5 rounded-sm font-mono uppercase tracking-wider text-sm flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Analyse..." : "Scorer le hook"}
        </button>
      </div>

      <div className="lg:col-span-3 border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6 min-h-[400px]">
        {!result ? (
          <div className="h-full flex items-center justify-center text-zinc-500 text-sm">Lance une analyse pour voir le score IA.</div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center justify-between border-b border-[#1E1E24] pb-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Score global</div>
                <div className="font-mono text-5xl font-bold mt-1" style={{ color: verdictColor(result.verdict) }} data-testid="score-result">{result.global_score}/100</div>
                <div className="font-mono text-sm uppercase tracking-wider mt-1" style={{ color: verdictColor(result.verdict) }}>{result.verdict}</div>
              </div>
            </div>

            {result.dimensions && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-3">Dimensions</div>
                <div className="space-y-2">
                  {result.dimensions.map((d, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between text-xs font-mono mb-1">
                        <span className="text-zinc-300 uppercase tracking-wider">{d.name}</span>
                        <span className="text-white">{d.score}</span>
                      </div>
                      <div className="h-1.5 bg-[#1E1E24] rounded-full overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${d.score}%`, background: d.score >= 80 ? "#10B981" : d.score >= 60 ? "#CCFF00" : d.score >= 40 ? "#F59E0B" : "#EF4444" }} />
                      </div>
                      {d.comment && <div className="text-[11px] text-zinc-500 mt-1 italic">{d.comment}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.strengths?.length > 0 && (
              <Section label="Points forts" color="#10B981" items={result.strengths} />
            )}
            {result.weaknesses?.length > 0 && (
              <Section label="Points faibles" color="#EF4444" items={result.weaknesses} />
            )}
            {result.improvements?.length > 0 && (
              <Section label="Améliorations" color="#F59E0B" items={result.improvements} />
            )}

            {result.rewritten_versions?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-[#CCFF00] mb-2">3 versions optimisées</div>
                <div className="space-y-2">
                  {result.rewritten_versions.map((v, i) => (
                    <div key={i} className="border border-[#CCFF00]/30 bg-[#CCFF00]/5 rounded-sm p-3 text-sm text-white">"{v}"</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, color, items }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color }}>{label}</div>
      <ul className="space-y-1">
        {items.map((i, idx) => <li key={idx} className="text-sm text-zinc-300">· {i}</li>)}
      </ul>
    </div>
  );
}

// ============== Heures Optimales ==============
const OPTIMAL_HOURS = {
  instagram: {
    fitness: ["07:00 - 09:00", "12:30 - 13:30", "20:00 - 22:00"],
    business: ["07:30 - 09:00", "12:00 - 13:00", "19:00 - 21:00"],
    lifestyle: ["09:00 - 11:00", "13:00 - 14:00", "20:00 - 23:00"],
    fashion: ["10:00 - 12:00", "14:00 - 16:00", "20:00 - 22:30"],
    food: ["07:00 - 08:30", "11:30 - 13:00", "18:00 - 20:00"],
    tech: ["08:00 - 10:00", "12:00 - 14:00", "21:00 - 23:00"],
  },
  tiktok: {
    fitness: ["06:00 - 08:00", "12:00 - 13:00", "19:00 - 23:00"],
    business: ["07:00 - 09:00", "12:00 - 13:30", "19:00 - 22:00"],
    lifestyle: ["10:00 - 12:00", "15:00 - 17:00", "20:00 - 23:30"],
    fashion: ["12:00 - 14:00", "18:00 - 20:00", "22:00 - 00:00"],
    food: ["11:00 - 12:30", "17:00 - 19:00", "21:00 - 23:00"],
    tech: ["09:00 - 11:00", "14:00 - 16:00", "20:00 - 23:00"],
  },
  youtube: {
    fitness: ["06:00 - 08:00", "18:00 - 20:00"],
    business: ["07:00 - 09:00", "17:00 - 19:00"],
    lifestyle: ["12:00 - 14:00", "20:00 - 22:00"],
    fashion: ["14:00 - 16:00", "20:00 - 22:00"],
    food: ["11:00 - 13:00", "18:00 - 20:00"],
    tech: ["09:00 - 11:00", "20:00 - 22:00"],
  },
};

function OptimalHours() {
  const [platform, setPlatform] = useState("instagram");
  const [niche, setNiche] = useState("business");
  const slots = OPTIMAL_HOURS[platform]?.[niche] || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6 space-y-4">
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#CCFF00] mb-1">// CONFIGURATION</div>
        <h2 className="font-display text-xl font-semibold text-white">Trouve tes heures</h2>
        <select value={platform} onChange={e => setPlatform(e.target.value)} data-testid="hours-platform"
          className="w-full bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
          <option value="instagram">Instagram</option>
          <option value="tiktok">TikTok</option>
          <option value="youtube">YouTube Shorts</option>
        </select>
        <select value={niche} onChange={e => setNiche(e.target.value)} data-testid="hours-niche"
          className="w-full bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
          <option value="fitness">Fitness</option>
          <option value="business">Business / Growth</option>
          <option value="lifestyle">Lifestyle</option>
          <option value="fashion">Fashion / Beauty</option>
          <option value="food">Food</option>
          <option value="tech">Tech</option>
        </select>
        <p className="text-xs text-zinc-500 leading-relaxed">Ces créneaux sont des moyennes pour audiences francophones. Test ton sweet spot personnel via l'onglet Tests.</p>
      </div>

      <div className="lg:col-span-2 border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-6">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-4">Heures optimales — {platform.toUpperCase()} / {niche.toUpperCase()}</div>
        <div className="space-y-3">
          {slots.map((slot, i) => (
            <div key={i} className="border border-[#CCFF00]/30 bg-[#CCFF00]/[0.03] rounded-sm p-4 flex items-center gap-4" data-testid={`slot-${i}`}>
              <div className="font-mono text-3xl font-medium text-[#CCFF00]">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <div className="font-mono text-xl text-white">{slot}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">
                  {i === 0 ? "Pic matin" : i === 1 ? "Pic midi" : "Pic prime time"}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 pt-5 border-t border-[#1E1E24] text-xs text-zinc-400 leading-relaxed">
          <span className="text-[#CCFF00]">Pro tip :</span> ne poste pas en dehors de ces créneaux pendant les 30 premiers jours d'un compte. L'algo construit ton "rythme" perçu.
        </div>
      </div>
    </div>
  );
}

// ============== Structures Virales ==============
const VIRAL_STRUCTURES = [
  {
    name: "Hook → Tension → Payoff → Loop",
    desc: "La structure universelle. Marche sur 90% des viraux 2026.",
    time: "0-1s hook · 1-3s tension · 3-5s payoff · 5-7s loop",
    example: "0:01 'Personne ne te dira ça' → 0:02 zoom écran → 0:04 révélation → 0:06 retour image initiale",
    bestFor: "TikTok, Reels IG"
  },
  {
    name: "Listicle Numéroté",
    desc: "5 erreurs / 3 secrets / 7 leçons. Save rate maximum.",
    time: "0-1s annonce du nombre · puis 1-2s par item · CTA save final",
    example: "'3 erreurs qui tuent ton reach' → liste chiffrée à l'écran → 'Save pour relire'",
    bestFor: "Reels IG (carousels aussi), YT Shorts"
  },
  {
    name: "Before / After",
    desc: "Transformation visible. Hook par contraste.",
    time: "0-1s avant · 1-3s transition · 3-5s après · 5-7s explication rapide",
    example: "Photo avant → cut → photo après → caption '30 jours pour ce résultat'",
    bestFor: "Fitness, beauty, business growth"
  },
  {
    name: "POV / Mise en situation",
    desc: "Le viewer s'identifie. Engagement émotionnel max.",
    time: "0-1s 'POV: tu...' · puis scénario · payoff",
    example: "'POV: t'as 19 ans et tu réalises que...' → narration",
    bestFor: "TikTok, Reels"
  },
  {
    name: "Hot Take Polarisant",
    desc: "Opinion tranchée. Comments en cascade.",
    time: "0-1s 'Hot take:' · 1-5s argument · 5-8s contre-argument anticipé",
    example: "'Hot take : la majorité des conseils growth en 2026 sont obsolètes.'",
    bestFor: "Threads, X, IG Reels"
  },
  {
    name: "Question Ouverte → Réponse",
    desc: "Le viewer cherche la réponse → reste jusqu'au bout.",
    time: "0-1s question · build-up jusqu'à la fin · payoff",
    example: "'Pourquoi 99% des comptes ne dépassent jamais 10K abonnés ?' → développement → réponse à 0:10",
    bestFor: "YT Shorts, IG Reels longs"
  },
  {
    name: "Storytelling Intime",
    desc: "Vulnérabilité contrôlée. Lien émotionnel fort.",
    time: "Setup 1-3s · conflit 3-8s · résolution 8-15s",
    example: "'À 23 ans j'avais 200€ sur mon compte. Voici ce qui a tout changé...'",
    bestFor: "Toutes plateformes longues"
  },
  {
    name: "Tutoriel rapide",
    desc: "Étape 1 → 2 → 3. Save rate gigantesque.",
    time: "0-1s hook 'Comment X' · puis steps · finir sur résultat",
    example: "'Comment doubler tes vues IG en 7 jours' → 3 étapes affichées → CTA",
    bestFor: "IG Reels, YT Shorts"
  },
];

function ViralStructures() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {VIRAL_STRUCTURES.map((s, i) => (
        <div key={i} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 hover:border-[#CCFF00]/40 transition" data-testid={`structure-${i}`}>
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-display text-lg font-semibold text-white">{s.name}</h3>
            <span className="text-[10px] font-mono text-[#CCFF00] shrink-0 ml-2">#{String(i + 1).padStart(2, "0")}</span>
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed mb-3">{s.desc}</p>
          <div className="space-y-2 pt-3 border-t border-[#1E1E24]">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#CCFF00]">Timing</div>
              <div className="font-mono text-xs text-zinc-300 mt-1">{s.time}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#00D4FF]">Exemple</div>
              <div className="text-xs text-zinc-300 italic mt-1">"{s.example}"</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Best for</div>
              <div className="text-xs text-zinc-400 mt-1">{s.bestFor}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============== Tracker Tendances ==============
function TrendsTracker() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", platform: "tiktok", type: "sound", status: "rising", expires_estimated: "", notes: "" });

  const load = async () => setList(await api.listTrends());
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.createTrend(form);
    toast.success("Trend ajoutée");
    setShowForm(false);
    setForm({ title: "", platform: "tiktok", type: "sound", status: "rising", expires_estimated: "", notes: "" });
    load();
  };

  const cycle = async (t) => {
    const next = { rising: "hot", hot: "declining", declining: "rising" }[t.status];
    await api.updateTrend(t.id, { status: next });
    load();
  };

  const del = async (id) => { await api.deleteTrend(id); load(); };

  const statusColor = (s) => ({ rising: "#00D4FF", hot: "#CCFF00", declining: "#EF4444" }[s] || "#A1A1AA");

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-400 max-w-2xl">Track tes trends en cours. Statut : <span className="text-[#00D4FF]">Rising</span> → <span className="text-[#CCFF00]">Hot</span> → <span className="text-[#EF4444]">Declining</span>. Clic sur le statut pour cycler.</p>
        <button onClick={() => setShowForm(s => !s)} data-testid="add-trend-btn"
          className="bg-[#CCFF00] text-black font-medium px-4 py-2 rounded-sm hover:bg-[#B3E600] flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Trend
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-5 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="trend-form">
          <input required placeholder="Titre / nom" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white md:col-span-2" />
          <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="tiktok">TikTok</option><option value="instagram">Instagram</option>
            <option value="youtube">YouTube</option><option value="threads">Threads</option><option value="x">X</option>
          </select>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="sound">Son</option><option value="concept">Concept</option>
            <option value="format">Format</option><option value="hashtag">Hashtag</option>
          </select>
          <input placeholder="Expire estimé (date)" value={form.expires_estimated} onChange={e => setForm({ ...form, expires_estimated: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white" />
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
            <option value="rising">Rising</option><option value="hot">Hot</option><option value="declining">Declining</option>
          </select>
          <textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white md:col-span-3" />
          <button type="submit" data-testid="submit-trend" className="bg-[#CCFF00] text-black px-5 py-2 rounded-sm font-medium text-sm md:col-span-3">Enregistrer</button>
        </form>
      )}

      {list.length === 0 ? (
        <div className="border border-dashed border-[#1E1E24] rounded-sm p-12 text-center">
          <Flame className="w-10 h-10 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <div className="text-zinc-500 text-sm">Aucune trend trackée. Ajoute la première.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map(t => (
            <div key={t.id} className="border border-[#1E1E24] bg-[#0A0A0C] rounded-sm p-4 hover:border-white/20" data-testid={`trend-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{t.title}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1">{t.platform} · {t.type}</div>
                </div>
                <button onClick={() => del(t.id)} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center justify-between mt-3">
                <button onClick={() => cycle(t)} data-testid={`cycle-${t.id}`}
                  className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm border" style={{ color: statusColor(t.status), borderColor: `${statusColor(t.status)}40`, background: `${statusColor(t.status)}10` }}>
                  ● {t.status}
                </button>
                {t.expires_estimated && <span className="text-[10px] font-mono text-zinc-500">Expire ≈ {t.expires_estimated}</span>}
              </div>
              {t.notes && <p className="text-xs text-zinc-400 mt-2">{t.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
